// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Hand-rolled SVG chart builders (no chart library). Each returns an SVG string
// sized in a viewBox; CSS scales it to the container width. Every data mark
// carries a [data-tip] for the shared hover tooltip.

import { anomalyColor } from './color';
import { attr } from './format';

export interface XY { x: number; y: number }

function niceStep(range: number, target: number): number {
  const raw = range / target;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  const step = n >= 5 ? 5 : n >= 2 ? 2 : 1;
  return step * pow;
}

export function ticks(min: number, max: number, target = 5): number[] {
  if (!(max > min)) return [min];
  const step = niceStep(max - min, target);
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

// ── Warming stripes ──────────────────────────────────────────────────────────
// Vertical bands, one per year, coloured by anomaly. Axis-less by design.
export function stripes(
  data: Array<{ y: number; a: number | null }>,
  opts: { w?: number; h?: number; domain?: number; label?: (d: { y: number; a: number | null }) => string } = {},
): string {
  const w = opts.w ?? 900, h = opts.h ?? 120, domain = opts.domain ?? 1.6;
  const n = data.length;
  if (!n) return '';
  const bw = w / n;
  const bars = data.map((d, i) => {
    const tip = opts.label ? opts.label(d) : `${d.y}: ${d.a == null ? 'no data' : (d.a > 0 ? '+' : '') + d.a.toFixed(2) + ' °C'}`;
    return `<rect x="${(i * bw).toFixed(2)}" y="0" width="${(bw + 0.6).toFixed(2)}" height="${h}" fill="${anomalyColor(d.a, domain)}" data-tip="${attr(tip)}" data-year="${d.y}"><title></title></rect>`;
  }).join('');
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" class="stripes-svg" role="img" aria-label="Warming stripes">${bars}</svg>`;
}

// ── Multi-series line chart with optional trend line ────────────────────────
export interface LineSeries { name: string; color: string; points: XY[]; width?: number; dash?: boolean }

export function lineChart(
  series: LineSeries[],
  opts: {
    w?: number; h?: number; yLabel?: string; xLabel?: string;
    yMin?: number; yMax?: number; zeroLine?: boolean; hoverYears?: boolean;
    tipFor?: (year: number) => string; unit?: string;
  } = {},
): string {
  const w = opts.w ?? 900, h = opts.h ?? 340;
  const m = { t: 14, r: 16, b: 34, l: 46 };
  const iw = w - m.l - m.r, ih = h - m.t - m.b;
  const allX = series.flatMap((s) => s.points.map((p) => p.x));
  const allY = series.flatMap((s) => s.points.map((p) => p.y));
  if (!allX.length) return '';
  const xmin = Math.min(...allX), xmax = Math.max(...allX);
  const ymin = opts.yMin ?? Math.min(...allY);
  const ymax = opts.yMax ?? Math.max(...allY);
  const sx = (x: number) => m.l + ((x - xmin) / (xmax - xmin || 1)) * iw;
  const sy = (y: number) => m.t + ih - ((y - ymin) / (ymax - ymin || 1)) * ih;

  let g = '';
  // y gridlines
  for (const t of ticks(ymin, ymax, 5)) {
    const yy = sy(t);
    g += `<line x1="${m.l}" y1="${yy.toFixed(1)}" x2="${m.l + iw}" y2="${yy.toFixed(1)}" class="grid"/>`;
    g += `<text x="${m.l - 6}" y="${(yy + 3.5).toFixed(1)}" class="axis-lbl" text-anchor="end">${t}</text>`;
  }
  // x labels
  for (const t of ticks(xmin, xmax, 6)) {
    const xx = sx(t);
    g += `<text x="${xx.toFixed(1)}" y="${h - 12}" class="axis-lbl" text-anchor="middle">${Math.round(t)}</text>`;
  }
  if (opts.zeroLine && ymin < 0 && ymax > 0) {
    g += `<line x1="${m.l}" y1="${sy(0).toFixed(1)}" x2="${m.l + iw}" y2="${sy(0).toFixed(1)}" class="zero-line"/>`;
  }
  for (const s of series) {
    const pts = s.points.filter((p) => Number.isFinite(p.y));
    if (!pts.length) continue;
    const d = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');
    g += `<path d="${d}" fill="none" stroke="${s.color}" stroke-width="${s.width ?? 2}" ${s.dash ? 'stroke-dasharray="5 4"' : ''} stroke-linejoin="round" stroke-linecap="round"/>`;
  }
  if (opts.yLabel) g += `<text x="${12}" y="${m.t + ih / 2}" class="axis-title" text-anchor="middle" transform="rotate(-90 12 ${m.t + ih / 2})">${opts.yLabel}</text>`;

  // Hover hit-columns per year (invisible, carry data-tip)
  if (opts.hoverYears && opts.tipFor) {
    const yrs = Array.from(new Set(allX)).sort((a, b) => a - b);
    const cw = iw / (yrs.length || 1);
    g += yrs.map((yr) => `<rect x="${(sx(yr) - cw / 2).toFixed(1)}" y="${m.t}" width="${cw.toFixed(1)}" height="${ih}" fill="transparent" data-tip="${attr(opts.tipFor!(yr))}" data-year="${yr}"/>`).join('');
  }
  return `<svg viewBox="0 0 ${w} ${h}" class="line-svg" role="img">${g}</svg>`;
}

// ── Vertical bars (hot days, per-year counts) ───────────────────────────────
export function vBars(
  data: Array<{ x: number; v: number; color?: string; tip?: string }>,
  opts: { w?: number; h?: number; color?: string; yMax?: number } = {},
): string {
  const w = opts.w ?? 900, h = opts.h ?? 300;
  const m = { t: 12, r: 12, b: 30, l: 40 };
  const iw = w - m.l - m.r, ih = h - m.t - m.b;
  if (!data.length) return '';
  const xmin = Math.min(...data.map((d) => d.x)), xmax = Math.max(...data.map((d) => d.x));
  const ymax = opts.yMax ?? Math.max(1, ...data.map((d) => d.v));
  const bw = Math.max(1, (iw / (xmax - xmin + 1)) * 0.86);
  const sx = (x: number) => m.l + ((x - xmin) / (xmax - xmin || 1)) * (iw - bw) + bw / 2;
  const sy = (v: number) => m.t + ih - (v / ymax) * ih;
  let g = '';
  for (const t of ticks(0, ymax, 4)) {
    const yy = sy(t);
    g += `<line x1="${m.l}" y1="${yy.toFixed(1)}" x2="${m.l + iw}" y2="${yy.toFixed(1)}" class="grid"/>`;
    g += `<text x="${m.l - 6}" y="${(yy + 3.5).toFixed(1)}" class="axis-lbl" text-anchor="end">${t}</text>`;
  }
  for (const t of ticks(xmin, xmax, 6)) g += `<text x="${sx(t).toFixed(1)}" y="${h - 10}" class="axis-lbl" text-anchor="middle">${Math.round(t)}</text>`;
  for (const d of data) {
    const x = sx(d.x) - bw / 2, y = sy(d.v);
    g += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, m.t + ih - y).toFixed(1)}" fill="${d.color ?? opts.color ?? '#d9614a'}" data-tip="${attr(d.tip ?? `${d.x}: ${d.v}`)}"/>`;
  }
  return `<svg viewBox="0 0 ${w} ${h}" class="bars-svg" role="img">${g}</svg>`;
}

// ── Sparkline (tiny inline line, no axes) ────────────────────────────────────
export function sparkline(values: Array<number | null>, opts: { w?: number; h?: number; color?: string } = {}): string {
  const w = opts.w ?? 120, h = opts.h ?? 26;
  const pts = values.map((v, i) => ({ i, v })).filter((p) => p.v != null) as Array<{ i: number; v: number }>;
  if (pts.length < 2) return `<svg viewBox="0 0 ${w} ${h}" class="spark"></svg>`;
  const n = values.length;
  const ymin = Math.min(...pts.map((p) => p.v)), ymax = Math.max(...pts.map((p) => p.v));
  const sx = (i: number) => (i / (n - 1)) * w;
  const sy = (v: number) => h - 2 - ((v - ymin) / (ymax - ymin || 1)) * (h - 4);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.i).toFixed(1)},${sy(p.v).toFixed(1)}`).join(' ');
  return `<svg viewBox="0 0 ${w} ${h}" class="spark" preserveAspectRatio="none"><path d="${d}" fill="none" stroke="${opts.color ?? '#b52a1f'}" stroke-width="1.5"/></svg>`;
}
